<script>
  import { onDestroy, onMount } from "svelte";
  import {
    login_feedback,
    login_feedback_default,
    portal_version,
  } from "./stores.js";

  export let unit_worker;
  export let socket_query;
  export let monitor_worker;
  export let login_db;
  export let geos_worker;
  export let travel_worker;
  export let trips_worker;
  export let routes_worker;
  export let login_state_ready = false;
  export let restoring_session = false;
  export let cancelSavedSessionRestore = async () => {};

  let usuario = "";
  let password = "";
  let auth_feedback_timer = null;
  let auth_timeout_timer = null;
  let alert_timer = null;
  let last_sensitive_alert = "";

  const MANUAL_LOGIN_SLOW_MS = 3500;
  const MANUAL_LOGIN_TIMEOUT_MS = 22000;

  function buildLoginFeedback(overrides = {}) {
    return {
      ...login_feedback_default,
      ...overrides,
    };
  }

  function clearFeedbackAlert() {
    login_feedback.update((current) => {
      if (!current.alertMessage) {
        return current;
      }
      return {
        ...current,
        alertMessage: "",
        alertType: "",
      };
    });
  }

  function scheduleManualSlowFeedback() {
    if (auth_feedback_timer) {
      clearTimeout(auth_feedback_timer);
    }
    auth_feedback_timer = setTimeout(() => {
      login_feedback.update((current) => {
        if (
          !current.active ||
          current.source != "manual" ||
          (current.phase != "authenticating" &&
            current.phase != "waiting-response")
        ) {
          return current;
        }
        return {
          ...current,
          phase: "waiting-response",
          title: "Esperando respuesta",
          detail:
            "El servidor todavía no confirma el acceso. Seguimos esperando por el socket...",
          slow: true,
        };
      });
    }, MANUAL_LOGIN_SLOW_MS);
  }

  function scheduleManualTimeoutFeedback() {
    if (auth_timeout_timer) {
      clearTimeout(auth_timeout_timer);
    }
    auth_timeout_timer = setTimeout(async () => {
      let still_waiting = false;
      login_feedback.update((current) => {
        if (
          !current.active ||
          current.source != "manual" ||
          (current.phase != "authenticating" &&
            current.phase != "waiting-response")
        ) {
          return current;
        }
        still_waiting = true;
        return current;
      });
      if (!still_waiting) {
        return;
      }
      password = "";
      try {
        await login_db.setItem("login_", "");
      } catch (_error) {}
      login_feedback.set(
        buildLoginFeedback({
          alertMessage:
            "El servidor no respondió a tiempo. Revisa la conexión e inténtalo de nuevo.",
          alertType: "alert-warning",
        })
      );
    }, MANUAL_LOGIN_TIMEOUT_MS);
  }

  function emitHelloToWorker(worker, loginPayload) {
    if (!worker || typeof worker.postMessage != "function") {
      return;
    }
    worker.postMessage({
      type: "emit",
      key: "hello",
      message: loginPayload,
    });
  }

  async function set_session() {
    if (login_busy || !usuario || !password) {
      return;
    }

    clearFeedbackAlert();
    login_feedback.set(
      buildLoginFeedback({
        active: true,
        phase: "authenticating",
        source: "manual",
        title: "Autenticando",
        detail: "Validando tus credenciales...",
      })
    );
    scheduleManualSlowFeedback();
    scheduleManualTimeoutFeedback();

    let id = new Date().valueOf().toString();
    try {
      const stored_id = await login_db.getItem("key");
      if (stored_id) {
        id = stored_id;
      } else {
        await login_db.setItem("key", id);
      }
    } catch (_error) {
      id = new Date().valueOf().toString();
    }

    const loginPayload = usuario + ":" + sha1(password);
    emitHelloToWorker(unit_worker, loginPayload);
    emitHelloToWorker(geos_worker, loginPayload);
    emitHelloToWorker(travel_worker, loginPayload);
    emitHelloToWorker(monitor_worker, loginPayload);
    emitHelloToWorker(trips_worker, loginPayload);
    emitHelloToWorker(routes_worker, loginPayload);

    socket_query.emit(
      "hello",
      loginPayload,
      id,
      $portal_version,
      window.screen.width + "x" + window.screen.height,
      {
        tab_id:
          sessionStorage.getItem("vector_query_tab_id") ||
          (() => {
            const tabId =
              Date.now().toString(36) +
              "-" +
              Math.random().toString(36).slice(2, 10);
            sessionStorage.setItem("vector_query_tab_id", tabId);
            return tabId;
          })(),
        route: window.location.pathname,
        visibility: document.visibilityState || "visible",
      }
    );
    try {
      await login_db.setItem("login_", loginPayload);
    } catch (_error) {}
  }

  function handleInput() {
    clearFeedbackAlert();
  }

  $: login_busy =
    $login_feedback.active &&
    $login_feedback.source == "manual" &&
    ($login_feedback.phase == "authenticating" ||
      $login_feedback.phase == "waiting-response");

  $: restore_feedback_active =
    restoring_session &&
    $login_feedback.active &&
    $login_feedback.phase == "restoring-session";

  $: show_status_screen = !login_state_ready || restore_feedback_active;

  $: status_screen_title = !login_state_ready
    ? "Preparando acceso"
    : $login_feedback.title || "Restaurando sesión";

  $: status_screen_detail = !login_state_ready
    ? "Verificando si existe una sesión guardada..."
    : $login_feedback.detail || "Reconectando con tu sesión guardada...";

  $: if (
    !(
      $login_feedback.active &&
      $login_feedback.source == "manual" &&
      ($login_feedback.phase == "authenticating" ||
        $login_feedback.phase == "waiting-response")
    ) &&
    auth_feedback_timer
  ) {
    clearTimeout(auth_feedback_timer);
    auth_feedback_timer = null;
  }

  $: if (
    !(
      $login_feedback.active &&
      $login_feedback.source == "manual" &&
      ($login_feedback.phase == "authenticating" ||
        $login_feedback.phase == "waiting-response")
    ) &&
    auth_timeout_timer
  ) {
    clearTimeout(auth_timeout_timer);
    auth_timeout_timer = null;
  }

  $: if ($login_feedback.alertMessage) {
    if (alert_timer) {
      clearTimeout(alert_timer);
    }
    alert_timer = setTimeout(() => {
      login_feedback.update((current) => {
        if (!current.alertMessage) {
          return current;
        }
        return {
          ...current,
          alertMessage: "",
          alertType: "",
        };
      });
    }, 4000);
  }

  $: if (
    $login_feedback.alertMessage &&
    ($login_feedback.alertType == "alert-danger" ||
      $login_feedback.alertType == "alert-warning")
  ) {
    const alert_signature =
      ($login_feedback.alertType || "") + ":" + $login_feedback.alertMessage;
    if (last_sensitive_alert != alert_signature) {
      password = "";
      last_sensitive_alert = alert_signature;
    }
  } else if (!$login_feedback.alertMessage) {
    last_sensitive_alert = "";
  }

  onMount(() => {
    window.document.body.classList.remove("central2");
    mdc.textField.MDCTextField.attachTo(document.querySelector("#usuario"));
    mdc.textField.MDCTextField.attachTo(document.querySelector("#password"));
  });

  onDestroy(() => {
    if (auth_feedback_timer) {
      clearTimeout(auth_feedback_timer);
    }
    if (auth_timeout_timer) {
      clearTimeout(auth_timeout_timer);
    }
    if (alert_timer) {
      clearTimeout(alert_timer);
    }
  });
</script>

{#if $login_feedback.alertMessage}
  <div
    class="alert-top alert {$login_feedback.alertType || 'alert-info'} mb-0 text-center"
    role="alert"
  >
    <strong>{$login_feedback.alertMessage}</strong>
  </div>
{/if}

{#if show_status_screen}
  <section class="status-screen" role="status" aria-live="polite">
    <div class="status-card">
      <div class="spinner" />
      <h2>{status_screen_title}</h2>
      <p>{status_screen_detail}</p>
      {#if restore_feedback_active && $login_feedback.slow}
        <p class="status-note">
          Si prefieres, puedes descartar la sesión guardada e ingresar con otra
          cuenta.
        </p>
      {/if}
      {#if restore_feedback_active}
        <button
          class="mdc-button mdc-button--outlined"
          type="button"
          on:click={cancelSavedSessionRestore}
        >
          <div class="mdc-button__ripple" />
          <span class="mdc-button__label">Ingresar con otra cuenta</span>
        </button>
      {/if}
    </div>
  </section>
{/if}

<main
  role="main"
  class="mdc-typography"
  style="width:100%; display:{show_status_screen ? 'none' : 'block'};"
  id="main"
>
  <img
    src="img/logo2.png"
    alt="logo"
    height="50"
    style="padding-bottom:10px;padding-top:10px;box-sizing:content-box;vertical-align:baseline"
  />
  <form on:submit|preventDefault={set_session} aria-busy={login_busy}>
    <div class="mdc-layout-grid">
      <div class="mdc-layout-grid__inner">
        <div class="mdc-layout-grid__cell">
          <label
            class="mdc-text-field mdc-text-field--outlined mdc-text-field--label-floating"
            style="width:300px"
            id="usuario"
          >
            <input
              type="text"
              class="mdc-text-field__input"
              aria-labelledby="usuario"
              required
              bind:value={usuario}
              disabled={login_busy}
              autocomplete="username"
              on:input={handleInput}
            />
            <span
              class="mdc-notched-outline mdc-notched-outline--upgraded mdc-notched-outline--notched"
            >
              <span class="mdc-notched-outline__leading" />
              <span class="mdc-notched-outline__notch" style="width: 56px;">
                <span
                  class="mdc-floating-label mdc-floating-label--required mdc-floating-label--float-above"
                  >Usuario</span
                >
              </span>
              <span class="mdc-notched-outline__trailing" />
            </span>
          </label>
        </div>
      </div>
      <div class="mdc-layout-grid__inner">
        <div class="mdc-layout-grid__cell">
          <label
            class="mdc-text-field mdc-text-field--outlined mdc-text-field--label-floating"
            style="width:300px"
            id="password"
          >
            <input
              type="password"
              class="mdc-text-field__input"
              aria-labelledby="password"
              required
              bind:value={password}
              autocomplete="current-password"
              disabled={login_busy}
              on:input={handleInput}
            />
            <span
              class="mdc-notched-outline mdc-notched-outline--upgraded mdc-notched-outline--notched"
            >
              <span class="mdc-notched-outline__leading" />
              <span class="mdc-notched-outline__notch" style="width: 76.25px;">
                <span
                  class="mdc-floating-label mdc-floating-label--required mdc-floating-label--float-above"
                  >Contraseña</span
                >
              </span>
              <span class="mdc-notched-outline__trailing" />
            </span>
          </label>
        </div>
      </div>
      <div
        class="mdc-layout-grid__inner"
        style="justify-content:flex-end;display:flex;width:300px;margin:auto"
      >
        <button
          class="mdc-button mdc-button--raised"
          type="submit"
          disabled={login_busy}
        >
          <div class="mdc-button__ripple" />
          <i class="material-icons mdc-button__icon" aria-hidden="true"
            >{login_busy ? "hourglass_top" : "bookmark"}</i
          >
          <span class="mdc-button__label"
            >{login_busy ? "Ingresando..." : "Ingresar"}</span
          >
        </button>
      </div>
      {#if login_busy}
        <div class="mdc-layout-grid__inner">
          <div class="mdc-layout-grid__cell">
            <div class="login-status-card" role="status" aria-live="polite">
              <div class="mini-spinner" />
              <div class="login-status-copy">
                <strong>{$login_feedback.title || "Autenticando"}</strong>
                <span>{$login_feedback.detail}</span>
                {#if $login_feedback.slow}
                  <small>
                    La conexión sigue activa; no cierres la pantalla mientras
                    esperamos respuesta.
                  </small>
                {/if}
              </div>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </form>
</main>

<style>
  @font-face {
    font-family: "Material Icons";
    font-style: normal;
    font-weight: 400;
    src: url(../css/flUhRq6tzZclQEJ-Vdg-IuiaDsNc.woff2) format("woff2");
  }
  .material-icons {
    font-family: "Material Icons";
    font-weight: normal;
    font-style: normal;
    font-size: 24px;
    line-height: 1;
    letter-spacing: normal;
    text-transform: none;
    display: inline-block;
    white-space: nowrap;
    word-wrap: normal;
    direction: ltr;
    -webkit-font-feature-settings: "liga";
    -webkit-font-smoothing: antialiased;
  }
  i {
    vertical-align: middle;
    padding-bottom: 3px;
  }
  .alert-top {
    position: fixed;
    top: 3rem;
    left: 50%;
    width: min(90vw, 32rem);
    transform: translateX(-50%);
    z-index: 1300;
  }
  .status-screen {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .status-card {
    width: min(90vw, 32rem);
    padding: 2rem 1.5rem;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.9rem;
  }
  .status-card h2 {
    margin: 0;
    font-size: 1.3rem;
    color: #0f172a;
  }
  .status-card p {
    margin: 0;
    line-height: 1.5;
    color: #475569;
  }
  .status-note {
    font-size: 0.92rem;
  }
  .login-status-card {
    width: min(90vw, 300px);
    margin: 0.5rem auto 0;
    padding: 0.85rem 0.95rem;
    border-radius: 0.9rem;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.12);
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    text-align: left;
  }
  .login-status-copy {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    color: #334155;
  }
  .login-status-copy strong {
    color: #0f172a;
  }
  .login-status-copy span,
  .login-status-copy small {
    line-height: 1.4;
  }
  button[disabled],
  input[disabled] {
    cursor: not-allowed;
    opacity: 0.7;
  }
  .spinner,
  .mini-spinner {
    border-radius: 50%;
    border-style: solid;
    border-color: #dbeafe;
    border-top-color: #2563eb;
    animation: spin 0.9s linear infinite;
  }
  .spinner {
    width: 4rem;
    height: 4rem;
    border-width: 0.38rem;
  }
  .mini-spinner {
    width: 1.2rem;
    height: 1.2rem;
    border-width: 0.18rem;
    flex: 0 0 auto;
    margin-top: 0.1rem;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
